import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  AlertTriangle, ArrowUpRight, Bell, Clock3, FileText, Mail, MessageCircle, Minus, Phone,
  Sparkles, Star, TrendingDown, TrendingUp, WalletCards, X,
} from "lucide-react";
import { money, daysLeft } from "../lib/finance";
import { computeHealth, healthGrade } from "../lib/health";
import { API } from "../lib/apiClient";
import { PageTitle, Kpi, Empty } from "./common";
import { DashboardAlertStrip } from "./DashboardAlertStrip";
import { NewRequestsCounter } from "./NewRequestsCounter";
import { VipCustomersCard } from "./VipCustomersCard";
import { InspectionAlerts } from "./InspectionAlerts";
import { PolicyTable } from "./Policies";
import { RenewalTargetCard } from "./RenewalTargetCard";
import { useUndeliverable } from "./SettingsExtras";

function UndeliverableBanner({ onOpen }) {
  const rows = useUndeliverable();
  if (!rows.length) return null;
  return (
    <div className="undeliverable-banner" data-testid="undeliverable-banner">
      <AlertTriangle size={16} />
      <span><strong>{rows.length} müşterinin e-posta adresine</strong> e-posta teslim edilemiyor ({rows.slice(0, 2).map((r) => r.name).join(", ")}{rows.length > 2 ? "…" : ""}).</span>
      <button className="text-button" onClick={onOpen} data-testid="undeliverable-banner-open">Adresleri düzelt →</button>
    </div>
  );
}

const YEAR = new Date().getFullYear();

function FavoritesStrip({ customers, policies, onOpenCustomer }) {
  const favs = (customers || []).filter((c) => c.favorite).slice(0, 10);
  if (favs.length === 0) return null;
  return (
    <section className="panel favorites-strip" data-testid="favorites-strip" style={{ marginBottom: 20 }}>
      <div className="panel-head"><div><h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><Star size={16} fill="#f59e0b" color="#f59e0b" />Favoriler</h2><p>Sık açtığın müşteriler</p></div></div>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", padding: "6px 2px 2px" }}>
        {favs.map((c) => {
          const count = policies.filter((p) => p.customer === c.name).length;
          return (
            <button
              key={c.id}
              data-testid={`favorite-card-${c.id}`}
              onClick={() => onOpenCustomer && onOpenCustomer(c)}
              style={{ flex: "0 0 auto", border: "1px solid var(--line)", background: "var(--surface)", borderRadius: 10, padding: "10px 14px", cursor: "pointer", textAlign: "left", minWidth: 170, transition: "0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#f59e0b")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--line)")}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Star size={13} fill="#f59e0b" color="#f59e0b" />
                <strong style={{ fontSize: 13 }}>{c.name}</strong>
              </div>
              <small style={{ color: "var(--muted)", fontSize: 11 }}>{count} poliçe · {c.phone || "-"}</small>
            </button>
          );
        })}
      </div>
    </section>
  );
}


function Sparkline({ points, color = "#1a5fe0", w = 160, h = 40 }) {
  if (!points || points.length < 2) {
    return <div className="health-spark-empty" data-testid="health-sparkline-empty">Bu hafta ilk kayıt</div>;
  }
  const scores = points.map((p) => p.score);
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 100);
  const range = Math.max(1, max - min);
  const stepX = w / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = h - ((p.score - min) / range) * (h - 6) - 3;
    return { x, y, score: p.score, week: p.weekStart };
  });
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const areaPath = `${path} L${w},${h} L0,${h} Z`;
  const last = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h + 8}`} width={w} height={h + 8} data-testid="health-sparkline" aria-hidden="true">
      <defs>
        <linearGradient id="hs-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#hs-grad)" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={i === coords.length - 1 ? 3 : 1.6} fill={color} />
      ))}
      <text x={Math.min(w - 4, last.x + 6)} y={Math.max(11, last.y - 4)} textAnchor={last.x > w - 30 ? "end" : "start"} fill={color} fontSize="10" fontWeight="700">{last.score}</text>
    </svg>
  );
}

function HealthScoreCard({ health, history, onOpenRenewals, onOpenReports }) {
  const { score, delta, trend, breakdown, stats, empty } = health;
  const grade = empty
    ? { label: "Portföyünüz boş", tone: "empty", color: "#64748b" }
    : healthGrade(score);
  const R = 52;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - Math.max(0, Math.min(100, score)) / 100);
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const deltaText = empty
    ? "İlk poliçenizi ekleyin, puan otomatik hesaplansın"
    : trend === "flat"
    ? "sabit"
    : `${delta > 0 ? "+" : ""}${delta} puan · son 30 gün`;
  const bars = [
    { key: "production", label: "Üretim", ...breakdown.production, color: "#1a5fe0" },
    { key: "renewal", label: "Yenileme", ...breakdown.renewal, color: "#3b82f6" },
    { key: "commission", label: "Komisyon", ...breakdown.commission, color: "#d97706" },
  ];
  return (
    <section className="panel health-card" data-testid="portfolio-health-card">
      <div className="health-head">
        <div className="health-eyebrow"><Sparkles size={13} /> PORTFÖY SAĞLIK PUANI</div>
        <div className="health-actions">
          {stats.upcomingRenewals > 0 && (
            <button className="text-button" data-testid="health-view-renewals" onClick={onOpenRenewals}>
              {stats.upcomingRenewals} yenileme <ArrowUpRight size={14} />
            </button>
          )}
          <button className="text-button" data-testid="health-view-reports" onClick={onOpenReports}>
            Rapora git <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
      <div className="health-body">
        <div className="health-gauge">
          <svg viewBox="0 0 120 120" width="132" height="132" aria-hidden="true">
            <circle cx="60" cy="60" r={R} fill="none" stroke="#e2e8f0" strokeWidth="10" />
            <circle
              cx="60" cy="60" r={R} fill="none" stroke={grade.color} strokeWidth="10"
              strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
              transform="rotate(-90 60 60)"
              style={{ transition: "stroke-dashoffset 800ms ease, stroke 300ms ease" }}
            />
          </svg>
          <div className="health-gauge-inner">
            <div className="health-score" data-testid="health-score-value">{score}</div>
            <div className="health-max">/100</div>
          </div>
        </div>
        <div className="health-meta">
          <div className="health-grade" style={{ color: grade.color }} data-testid="health-grade-label">{grade.label}</div>
          <div className="health-trend" data-testid="health-trend">
            <TrendIcon size={15} className={`trend-${trend}`} />
            <span>{deltaText}</span>
          </div>
          <div className="health-stats">
            <div><strong>{stats.newPolicies90d}</strong><span>son 90 gün poliçe</span></div>
            <div><strong>{stats.followedRenewals}/{stats.upcomingRenewals}</strong><span>yenileme takipte</span></div>
            <div><strong>{stats.missedRenewals}</strong><span>kaçırılan yenileme</span></div>
          </div>
          <div className="health-spark">
            <div className="health-spark-head">
              <span className="health-spark-label">Son {history?.length || 0} hafta trendi</span>
            </div>
            <Sparkline points={history} color={grade.color} />
          </div>
        </div>
        <div className="health-breakdown">
          {bars.map((b) => {
            const pct = b.max ? Math.round((b.score / b.max) * 100) : 0;
            return (
              <div className="health-bar" key={b.key} data-testid={`health-bar-${b.key}`}>
                <div className="health-bar-head">
                  <span className="health-bar-label">{b.label}</span>
                  <span className="health-bar-value">{b.score}/{b.max}</span>
                </div>
                <div className="health-bar-track">
                  <div className="health-bar-fill" style={{ width: `${pct}%`, background: b.color }} />
                </div>
                <div className="health-bar-note">{b.note}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RenewalAlerts({ onOpenPolicies }) {
  const [data, setData] = useState({ buckets: { "3": [], "15": [], "30": [] } });
  const [loading, setLoading] = useState(true);
  const [busySend, setBusySend] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/renewals`);
      setData(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (policyId) => {
    try {
      await axios.post(`${API}/notifications/renewals/dismiss`, { policyId });
      // Optimistic remove
      setData((prev) => ({
        ...prev,
        buckets: Object.fromEntries(
          Object.entries(prev.buckets || {}).map(([k, arr]) => [k, arr.filter((p) => p.id !== policyId)])
        ),
      }));
    } catch (e) {
      // silent
    }
  };

  const sendEmail = async () => {
    setBusySend(true);
    setToast("");
    try {
      const { data } = await axios.post(`${API}/reminders/renewals/send-now`);
      if (data.sent) {
        setToast(`Hatırlatma e-postası gönderildi · ${data.total} poliçe`);
      } else if (data.reason === "no-renewals") {
        setToast("Şu anda hatırlatma gönderilecek poliçe bulunmuyor.");
      } else if (data.reason === "already-sent-today") {
        setToast("Bugün için hatırlatma zaten gönderildi.");
      } else {
        setToast(data.reason || "Bilinmeyen durum");
      }
    } catch (e) {
      setToast(e?.response?.data?.detail || "E-posta gönderilemedi");
    } finally {
      setBusySend(false);
      setTimeout(() => setToast(""), 5000);
    }
  };

  const buckets = [
    { days: 3, tone: "danger", label: "3 gün", note: "Acil arayın", icon: AlertTriangle },
    { days: 15, tone: "warning", label: "15 gün", note: "Bu hafta yenileyin", icon: Clock3 },
    { days: 30, tone: "info", label: "30 gün", note: "Planlamaya alın", icon: Bell },
  ];

  const total = buckets.reduce((s, b) => s + (data.buckets?.[b.days]?.length || 0), 0);

  if (loading || total === 0) return null;

  const cleanPhone = (p) => (p || "").replace(/\D/g, "");

  return (
    <section className="renewal-alerts" data-testid="renewal-alerts">
      <div className="renewal-alerts-head">
        <div>
          <div className="eyebrow"><Bell size={13} /> YENİLEME UYARILARI</div>
          <h2 data-testid="renewal-alerts-total">{total} poliçenin yenileme tarihi yaklaşıyor</h2>
          <p>3, 15 ve 30 gün eşiklerinde uyarı gösterilir. Her sabah 07:00'de otomatik e-posta hatırlatması gider.</p>
        </div>
        <button
          className="outline-button"
          data-testid="renewal-send-email-button"
          onClick={sendEmail}
          disabled={busySend}
        >
          <Mail size={15} /> {busySend ? "Gönderiliyor…" : "Şimdi e-posta gönder"}
        </button>
      </div>
      {toast && <div className="renewal-toast" data-testid="renewal-toast">{toast}</div>}
      <div className="renewal-buckets">
        {buckets.map((b) => {
          const items = data.buckets?.[b.days] || [];
          if (items.length === 0) return null;
          const Icon = b.icon;
          return (
            <div key={b.days} className={`renewal-bucket renewal-${b.tone}`} data-testid={`renewal-bucket-${b.days}`}>
              <div className="renewal-bucket-head">
                <Icon size={16} />
                <strong>{b.label} kaldı</strong>
                <span className="renewal-count">{items.length}</span>
                <em>{b.note}</em>
              </div>
              <div className="renewal-cards">
                {items.map((p) => {
                  const phoneDigits = cleanPhone(p.customerPhone);
                  return (
                    <div key={p.id} className="renewal-card" data-testid={`renewal-card-${p.id}`}>
                      <div className="renewal-card-info">
                        <strong data-testid={`renewal-customer-${p.id}`}>{p.customer || "-"}{p.plate ? <span className="plate-chip">{p.plate}</span> : null}{p.pdfCount > 1 ? <span data-testid={`renewal-zeyil-${p.id}`} title={`${p.pdfCount} PDF ekli (zeyil mevcut)`} style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#fef3c7", color: "#92400e", fontWeight: 700, letterSpacing: 0.3 }}>ZEYİL</span> : null}</strong>
                        <span>{p.branch} · No: {p.policeNo}</span>
                        <small><b>Bitiş:</b> {p.end}</small>
                      </div>
                      <div className="renewal-card-actions">
                        {phoneDigits && (
                          <a
                            className="mini-btn wa"
                            data-testid={`renewal-wa-${p.id}`}
                            href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(`Merhaba ${p.customer}, ${p.branch} branşındaki ${p.plate ? p.plate + " plakalı aracınıza ait " : ""}${p.policeNo} numaralı poliçenizin bitiş tarihi ${p.end}. Yenileme için görüşebilir miyiz?`)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <MessageCircle size={13} /> WhatsApp
                          </a>
                        )}
                        {phoneDigits && (
                          <a className="mini-btn call" data-testid={`renewal-call-${p.id}`} href={`tel:${phoneDigits}`}>
                            <Phone size={13} /> Ara
                          </a>
                        )}
                        <button
                          className="mini-btn dismiss"
                          data-testid={`renewal-dismiss-${p.id}`}
                          onClick={() => dismiss(p.id)}
                          title="Bugün için kapat"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <button className="text-button renewal-view-all" data-testid="renewal-view-all" onClick={onOpenPolicies}>
        Tüm yenilemeleri gör <ArrowUpRight size={14} />
      </button>
    </section>
  );
}

function CorruptPdfBanner({ onOpenPolicies }) {
  const [data, setData] = useState({ count: 0, policies: [] });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("corrupt-pdf-banner-dismissed") === "1");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/corrupt-pdfs`);
        setData(data);
      } catch { /* silent — endpoint may not exist on older backend */ }
      setLoading(false);
    })();
  }, []);

  if (loading || dismissed || data.count === 0) return null;

  const dismiss = () => {
    sessionStorage.setItem("corrupt-pdf-banner-dismissed", "1");
    setDismissed(true);
  };

  return (
    <section className="corrupt-pdf-banner" data-testid="corrupt-pdf-banner" style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <AlertTriangle size={22} color="#b45309" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ color: "#92400e", fontSize: 14, display: "block", marginBottom: 4 }}>
            Önemli: {data.count} poliçede PDF ekleri kayıp olabilir
          </strong>
          <p style={{ color: "#78350f", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            Eski bir depolama hatası nedeniyle, aynı poliçeye birden fazla PDF yüklendiğinde ikinci yükleme birinciyi (ana poliçe) depodan silmiş olabilir. Aşağıdaki poliçelerde ana poliçeyi yeniden yüklemen gerekir.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <button className="outline-button" data-testid="corrupt-pdf-expand" onClick={() => setExpanded((v) => !v)} style={{ fontSize: 12, padding: "6px 10px", background: "#fff" }}>
              {expanded ? "Listeyi gizle" : "Etkilenen poliçeleri göster"}
            </button>
            <button className="outline-button" data-testid="corrupt-pdf-goto-policies" onClick={onOpenPolicies} style={{ fontSize: 12, padding: "6px 10px", background: "#fff" }}>
              Poliçelere git
            </button>
            <button className="text-button" data-testid="corrupt-pdf-dismiss" onClick={dismiss} style={{ fontSize: 11, color: "#92400e" }}>
              Bu oturumda bir daha gösterme
            </button>
          </div>
          {expanded && (
            <div style={{ marginTop: 14, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 10, maxHeight: 280, overflowY: "auto" }}>
              {data.policies.map((p) => (
                <div key={p.id} data-testid={`corrupt-pdf-row-${p.id}`} style={{ padding: "8px 0", borderBottom: "1px solid #fde68a", fontSize: 12 }}>
                  <strong style={{ color: "#78350f" }}>{p.policeNo}</strong>
                  <span style={{ color: "#92400e", marginLeft: 8 }}>{p.customer} · {p.branch} · {p.company}</span>
                  <div style={{ color: "#b45309", fontSize: 11, marginTop: 3 }}>
                    {p.attachments.length} ek · {p.attachments.map((a) => a.filename).join(", ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="icon-button" data-testid="corrupt-pdf-close" onClick={dismiss} style={{ color: "#92400e" }}><X size={16} /></button>
      </div>
    </section>
  );
}


export function Dashboard({ policies, customers, branches, settings, followUps, upcoming, totalPremium, totalCommissionPersonal, vehicles = [], quotes = [], onOpenRequests, onPrepareRequest, setPage, setModal, onOpenCustomer, onOpenPolicy, onPolicyUpdated }) {
  const branchList = Array.from(new Set([...branches.map((b) => b.name), ...policies.map((p) => p.branch)])).filter(Boolean);
  const chart = branchList
    .map((name) => ({
      branch: name.length > 10 ? name.slice(0, 10) + "…" : name,
      total: policies.filter((p) => p.branch === name).reduce((s, p) => s + Number(p.premium || 0), 0),
    }))
    .filter((x) => x.total > 0);

  const health = useMemo(
    () => computeHealth(policies, followUps || [], branches, settings),
    [policies, followUps, branches, settings]
  );
  const [healthHistory, setHealthHistory] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Snapshot current week (idempotent), then fetch history
        if (!health.empty) {
          await axios.post(`${API}/reports/health-score/snapshot`, {
            score: health.score,
            breakdown: health.breakdown,
            stats: health.stats,
          });
        }
        const { data } = await axios.get(`${API}/reports/health-score/history?weeks=12`);
        if (!cancelled) setHealthHistory(data.items || []);
      } catch (e) {
        // Non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [health.score, health.empty]);

  return (
    <>
      <PageTitle
        eyebrow={`GENEL BAKIŞ · ${YEAR}`}
        title="Portföyünüz bugün"
        description="Poliçeleriniz, yaklaşan yenilemeleriniz ve hak edişleriniz tek yerde."
        action="Yeni poliçe"
        onAction={() => setModal("policy")}
        actionTest="dashboard-add-policy-button"
      />
      <NewRequestsCounter quotes={quotes} onOpen={onOpenRequests} onPrepare={onPrepareRequest} />
      <HealthScoreCard health={health} history={healthHistory} onOpenRenewals={() => setPage("renewals")} onOpenReports={() => setPage("reports")} />
      <DashboardAlertStrip policies={policies} followUps={followUps} vehicles={vehicles} onOpenRenewals={() => setPage("renewals")} onOpenPolicies={() => setPage("policies")} onOpenVehicles={() => setPage("vehicles")} />
      <RenewalAlerts onOpenPolicies={() => setPage("renewals")} />
      <InspectionAlerts vehicles={vehicles} customers={customers} onOpenRenewals={() => setPage("renewals")} />
      <CorruptPdfBanner onOpenPolicies={() => setPage("policies")} />
      <FavoritesStrip customers={customers} policies={policies} onOpenCustomer={onOpenCustomer} />
      <UndeliverableBanner onOpen={() => setPage("preferences")} />
      <RenewalTargetCard policies={policies} customers={customers} onOpenRenewals={() => setPage("renewals")} onOpenCustomer={onOpenCustomer} onOpenPolicy={onOpenPolicy} onPolicyUpdated={onPolicyUpdated} />
      <VipCustomersCard policies={policies} customers={customers} branches={branches} settings={settings} onOpenCustomer={onOpenCustomer} />
      <div className="kpi-grid">
        <Kpi label="Toplam poliçe" value={policies.length} detail="Portföydeki tüm kayıtlar" icon={FileText} color="navy" />
        <Kpi label="Aktif prim üretimi" value={money(totalPremium)} detail={`Toplam brüt üretim · ${YEAR}`} icon={ArrowUpRight} color="teal" />
        <Kpi label="Kişisel hak ediş" value={money(totalCommissionPersonal)} detail={`Net → acente %${settings.personalShare} pay`} icon={WalletCards} color="amber" />
        <Kpi label="Yaklaşan yenileme" value={upcoming.length} detail="Önümüzdeki 60 gün" icon={Clock3} color="rose" />
      </div>
      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-head">
            <div><h2>Branş bazında üretim</h2><p>Prim üretiminizin dağılımı</p></div>
            <button className="text-button" data-testid="view-annual-report-button" onClick={() => setPage("reports")}>Detaylı rapor <ArrowUpRight size={15} /></button>
          </div>
          <div className="chart-wrap">
            {chart.length === 0 ? <Empty text="Henüz üretim verisi yok" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: -15, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#e8edf3" />
                  <XAxis dataKey="branch" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}K`} />
                  <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="total" fill="#1a5fe0" radius={[4, 4, 0, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>
        <section className="panel renewal-panel">
          <div className="panel-head">
            <div><h2>Yaklaşan yenilemeler</h2><p>Takip gerektiren poliçeler</p></div>
            <button className="icon-link" data-testid="view-renewals-button" onClick={() => setPage("renewals")}><ArrowUpRight size={17} /></button>
          </div>
          {upcoming.slice(0, 4).map((p) => (
            <div className="renewal-row" key={p.id}>
              <div className="branch-icon">{p.branch ? p.branch[0] : "?"}</div>
              <div className="row-main"><strong>{p.customer}</strong><span>{p.branch} · {p.policeNo}</span></div>
              <div className={`days ${daysLeft(p.end) <= 15 ? "danger" : "warning"}`}>{Math.max(0, daysLeft(p.end))} gün</div>
            </div>
          ))}
          {upcoming.length === 0 && <Empty text="Yaklaşan yenileme bulunmuyor" />}
        </section>
      </div>
      <div className="dashboard-grid lower">
        <section className="panel">
          <div className="panel-head">
            <div><h2>Son poliçeler</h2><p>En son eklenen kayıtlar</p></div>
            <button className="text-button" data-testid="view-all-policies-button" onClick={() => setPage("policies")}>Tümünü gör <ArrowUpRight size={15} /></button>
          </div>
          <PolicyTable policies={policies.slice(0, 4)} branches={branches} settings={settings} compact />
        </section>
        <section className="panel mini-customers">
          <div className="panel-head">
            <div><h2>Müşteriler</h2><p>Aktif portföyünüz</p></div>
            <button className="text-button" data-testid="view-all-customers-button" onClick={() => setPage("customers")}>Tümünü gör <ArrowUpRight size={15} /></button>
          </div>
          {customers.slice(0, 4).map((c) => (
            <div className="customer-row" key={c.id}>
              <div className="avatar light">{c.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
              <div><strong>{c.name}</strong><span>{policies.filter((p) => p.customer === c.name).length} poliçe · {c.type}</span></div>
            </div>
          ))}
          {customers.length === 0 && <Empty text="Henüz müşteri yok" />}
        </section>
      </div>
    </>
  );
}

